import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyMobileBar } from "@/components/StickyMobileBar";
import { SORTED_POSTS } from "@/lib/blog/posts";
import { ArrowRight, CalendarDays, Clock } from "lucide-react";

const URL = "https://sepremiumcabs.com/blog";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Melbourne Taxi & Airport Transfer Guides — SE Premium Cabs" },
      {
        name: "description",
        content:
          "Practical guides to Melbourne taxi fares, Tullamarine airport transfers, maxi cabs and group travel — written by a working Melbourne cab operator.",
      },
      { property: "og:title", content: "Melbourne Taxi & Airport Transfer Guides — SE Premium Cabs" },
      {
        property: "og:description",
        content:
          "Fares, terminals, vehicle sizes and booking tips for Melbourne Airport transfers and cab travel across Melbourne.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://sepremiumcabs.com/og-image.jpg" },
      { name: "twitter:image", content: "https://sepremiumcabs.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "SE Premium Cabs Guides",
          url: URL,
          publisher: { "@type": "Organization", name: "SE Premium Cabs", url: "https://sepremiumcabs.com" },
          blogPost: SORTED_POSTS.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            datePublished: p.date,
            url: `${URL}/${p.slug}`,
          })),
        }),
      },
    ],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto max-w-[1100px] px-6 py-10">
        <p className="text-xs font-semibold tracking-[0.3em] text-primary">GUIDES</p>
        <h1 className="mt-3 font-serif text-4xl font-bold tracking-tight md:text-5xl">
          MELBOURNE CAB &amp; AIRPORT TRANSFER GUIDES
        </h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Straight answers on fares, terminals, vehicle sizes and booking — from a Melbourne
          operator running airport transfers every day of the year.
        </p>

        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {SORTED_POSTS.map((p) => (
            <article
              key={p.slug}
              className="flex flex-col rounded-xl border border-border/60 bg-card p-6"
            >
              <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />
                  {new Date(p.date).toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {p.readMinutes} min read
                </span>
              </div>
              <h2 className="mt-3 font-serif text-xl font-bold leading-snug">
                <Link to="/blog/$slug" params={{ slug: p.slug }} className="hover:text-primary">
                  {p.title}
                </Link>
              </h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{p.excerpt}</p>
              <Link
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="mt-5 inline-flex items-center gap-2 text-xs font-bold tracking-widest text-primary"
              >
                READ GUIDE <ArrowRight className="h-3 w-3" />
              </Link>
            </article>
          ))}
        </div>
      </main>
      <SiteFooter />
      <div className="h-16 lg:hidden" />
      <StickyMobileBar />
    </div>
  );
}
